import React, { useEffect, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  createFilterOptions,
} from '@mui/material';

import { CUG_TIER_LABELS } from '~/components/GHAPropertyManagement/constants';

import { useGHAChains } from '~/hooks/apis/connection-google/useGHAChains';

import type {
  GHACugTierChainRule,
  GHACugTierChainRuleTier,
  UpsertGHACugTierChainRuleBody,
} from '~/services/ConnectionGoogleService';

const TIER_OPTIONS: Array<GHACugTierChainRuleTier> = ['tier_1', 'tier_2'];

// ~5k chains; capped as on GHA Property Management so opening the list stays fast.
const chainFilterOptions = createFilterOptions<string>({ limit: 500 });

interface Props {
  open: boolean;
  /** Present = change this chain's tier; the chain name is the key, so it cannot change. */
  initialRule?: GHACugTierChainRule | null;
  isSaving: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (input: UpsertGHACugTierChainRuleBody) => void;
}

export default function CugTierChainRuleDialog({ open, initialRule, isSaving, submitError, onClose, onSubmit }: Props) {
  const [chainName, setChainName] = useState<string | null>(null);
  const [cugTier, setCugTier] = useState<GHACugTierChainRuleTier>('tier_1');
  const [reason, setReason] = useState('');
  const { data: chainOptions, isLoading: chainsLoading } = useGHAChains();

  useEffect(() => {
    if (open) {
      setChainName(initialRule?.chainName ?? null);
      setCugTier(initialRule?.cugTier === 'tier_2' ? 'tier_2' : 'tier_1');
      setReason(initialRule?.reason ?? '');
    }
  }, [open, initialRule]);

  const isEdit = initialRule != null;

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit CUG tier chain' : 'New CUG tier chain'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            size="small"
            options={chainOptions ?? []}
            filterOptions={chainFilterOptions}
            loading={chainsLoading}
            value={chainName}
            disabled={isEdit}
            onChange={(_, next) => setChainName(next)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Chain"
                helperText="The chain name as bedbank records it. Matched as a whole name, ignoring case."
              />
            )}
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="cug-tier-chain-rule-tier">Tier</InputLabel>
            <Select
              labelId="cug-tier-chain-rule-tier"
              label="Tier"
              value={cugTier}
              onChange={(event) => setCugTier(event.target.value)}
            >
              {TIER_OPTIONS.map((tier) => (
                <MenuItem key={tier} value={tier}>
                  {CUG_TIER_LABELS[tier]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Reason (optional)"
            placeholder="CUG tier policy, section 3"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            inputProps={{ maxLength: 500 }}
            fullWidth
          />
          {submitError != null && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSaving || chainName == null}
          onClick={() =>
            chainName != null &&
            onSubmit({ chainName, cugTier, reason: reason.trim() === '' ? undefined : reason.trim() })
          }
        >
          {isEdit ? 'Save' : 'Create chain'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
